import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, IconButton, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HistoryIcon from '@mui/icons-material/History';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import LiveTvOutlinedIcon from '@mui/icons-material/LiveTvOutlined';
import type { IInfluencer, IPriceCard, IPlatformStats } from '../../../redux/buzzbridge/types';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import {
    CREATOR_METRIC_GOLD,
    CREATOR_METRIC_ON_VARIANT,
    CREATOR_METRIC_RADIUS_BTN,
    CREATOR_METRIC_SURFACE_HI,
    CREATOR_METRIC_SURFACE_LOW,
} from './creatorProfileMetricTokens';
import {
    PLATFORM_DELIVERABLES,
    RATE_CARD_PLATFORMS,
    type PriceCardContentKey,
    type RateCardPlatformId,
    getStoredDeliverableRate,
    rateToPricingTuple,
} from '../utils/rateCardPlatformConfig';
import { formatFollowersForInsight, formatUsdRange, suggestRateRange } from '../utils/rateCardSuggestions';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph, PLATFORM_BRAND } from '../SocialPlatformSvgs';

const SECONDARY = '#00fdc1';

function DeliverableIcon({ name }: { name: string }) {
    const sx = { fontSize: 22, color: CREATOR_METRIC_GOLD };
    switch (name) {
        case 'history':
            return <HistoryIcon sx={sx} />;
        case 'image':
            return <ImageOutlinedIcon sx={sx} />;
        case 'play_circle':
            return <VideocamOutlinedIcon sx={sx} />;
        case 'smart_display':
            return <LiveTvOutlinedIcon sx={sx} />;
        default:
            return <MovieOutlinedIcon sx={sx} />;
    }
}

function PlatformBrandGlyph({ id, size = 22 }: { id: RateCardPlatformId; size?: number }) {
    const sx = { width: size, height: size, flexShrink: 0 };
    if (id === 'instagram') {
        return <InstagramGlyph sx={sx} color={PLATFORM_BRAND.instagram.primary} />;
    }
    if (id === 'tiktok') {
        return <TiktokGlyph sx={sx} color={PLATFORM_BRAND.tiktok.primary} />;
    }
    return <YoutubeGlyph sx={sx} color={PLATFORM_BRAND.youtube.primary} />;
}

export interface RateCardBuilderModalProps {
    open: boolean;
    onClose: () => void;
    influencer: IInfluencer;
    initialPlatform: RateCardPlatformId;
    onSave: (platform: RateCardPlatformId, pricing: Partial<IPriceCard>) => void;
    saving?: boolean;
}

export function RateCardBuilderModal({
    open,
    onClose,
    influencer,
    initialPlatform,
    onSave,
    saving,
}: RateCardBuilderModalProps) {
    const muiTheme = useTheme();
    const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const [step, setStep] = useState<1 | 2>(2);
    const [platform, setPlatform] = useState<RateCardPlatformId>(initialPlatform);
    const [draftRates, setDraftRates] = useState<Partial<Record<PriceCardContentKey, string>>>({});

    const availablePlatforms = useMemo(() => {
        return RATE_CARD_PLATFORMS.filter(p => {
            const stats = influencer[p.id] as IPlatformStats | undefined;
            return Boolean(stats?.username || stats?.followers != null);
        });
    }, [influencer]);

    const platformStats = influencer[platform] as IPlatformStats | undefined;
    const followers = platformStats?.followers ?? 0;
    const deliverables = PLATFORM_DELIVERABLES[platform];

    useEffect(() => {
        if (!open) return;
        setPlatform(initialPlatform);
        setStep(2);
    }, [open, initialPlatform]);

    useEffect(() => {
        if (!open) return;
        const pricing = platformStats?.pricing;
        const defs = PLATFORM_DELIVERABLES[platform];
        const next: Partial<Record<PriceCardContentKey, string>> = {};
        for (const d of defs) {
            const stored = getStoredDeliverableRate(pricing, d.key);
            const suggested = suggestRateRange(followers, d.key);
            next[d.key] = String(stored > 0 ? stored : suggested.midpoint);
        }
        setDraftRates(next);
    }, [open, platform, platformStats?.pricing, followers]);

    const packageTotal = useMemo(() => {
        return deliverables.reduce((sum, d) => {
            const n = Number.parseFloat(draftRates[d.key] ?? '0');
            return sum + (Number.isFinite(n) ? n : 0);
        }, 0);
    }, [deliverables, draftRates]);

    const handleSave = useCallback(() => {
        const pricing: Partial<IPriceCard> = {};
        for (const d of deliverables) {
            const n = Number.parseFloat(draftRates[d.key] ?? '0');
            if (Number.isFinite(n) && n > 0) {
                pricing[d.key] = rateToPricingTuple(n);
            }
        }
        onSave(platform, pricing);
    }, [deliverables, draftRates, onSave, platform]);

    const fmtUsd = (n: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={fullScreen}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: '#0e0e0e',
                    backgroundImage: 'none',
                    borderRadius: fullScreen ? 0 : 2,
                    maxHeight: fullScreen ? '100%' : 'min(92vh, 900px)',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            <Box
                sx={{
                    flexShrink: 0,
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <IconButton onClick={onClose} aria-label="Close" sx={{ color: CREATOR_METRIC_GOLD }}>
                        {fullScreen ? <CloseIcon /> : <ArrowBackIcon />}
                    </IconButton>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 800,
                            fontSize: { xs: 18, sm: 20 },
                            color: CREATOR_METRIC_GOLD,
                        }}>
                        Rate card builder
                    </Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 10,
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            color: CREATOR_METRIC_ON_VARIANT,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        }}>
                        Progress
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: '#fff' }}>
                            Step {step} of 2
                        </Typography>
                        <Box
                            sx={{
                                width: 96,
                                height: 6,
                                borderRadius: 999,
                                bgcolor: CREATOR_METRIC_SURFACE_HI,
                                overflow: 'hidden',
                            }}>
                            <Box
                                sx={{
                                    width: step === 1 ? '50%' : '100%',
                                    height: '100%',
                                    bgcolor: SECONDARY,
                                    boxShadow: '0 0 8px rgba(0,253,193,0.45)',
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                {step === 1 ? (
                    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                fontSize: { xs: 22, sm: 26 },
                                color: '#fff',
                                mb: 0.5,
                            }}>
                            Select platform
                        </Typography>
                        <Typography sx={{ color: CREATOR_METRIC_ON_VARIANT, fontSize: 14, mb: 3 }}>
                            Choose a channel to configure your UGC rates.
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                gap: 2,
                            }}>
                            {availablePlatforms.map(p => {
                                const selected = platform === p.id;
                                const stats = influencer[p.id] as IPlatformStats | undefined;
                                return (
                                    <Box
                                        key={p.id}
                                        component="button"
                                        type="button"
                                        onClick={() => {
                                            setPlatform(p.id);
                                            setStep(2);
                                        }}
                                        sx={{
                                            position: 'relative',
                                            cursor: 'pointer',
                                            border: selected ? '1px solid transparent' : '1px solid rgba(72,72,71,0.3)',
                                            borderRadius: '12px',
                                            p: selected ? '1px' : 0,
                                            background: selected
                                                ? `linear-gradient(135deg, ${CREATOR_METRIC_GOLD}, #feb700)`
                                                : 'transparent',
                                            boxShadow: selected ? '0 0 20px rgba(255,201,101,0.15)' : 'none',
                                        }}>
                                        <Box
                                            sx={{
                                                bgcolor: selected
                                                    ? CREATOR_METRIC_SURFACE_LOW
                                                    : CREATOR_METRIC_SURFACE_LOW,
                                                borderRadius: '11px',
                                                p: 3,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                width: '100%',
                                                border: 'none',
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: '50%',
                                                    bgcolor: 'rgba(255,201,101,0.1)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}>
                                                <PlatformBrandGlyph id={p.id} size={28} />
                                            </Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontWeight: 700,
                                                    color: selected ? '#fff' : CREATOR_METRIC_ON_VARIANT,
                                                }}>
                                                {p.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: CREATOR_METRIC_ON_VARIANT }}>
                                                {formatFollowersForInsight(stats?.followers ?? 0)} followers
                                            </Typography>
                                            {selected ? (
                                                <CheckCircleIcon
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 12,
                                                        right: 12,
                                                        color: CREATOR_METRIC_GOLD,
                                                        fontSize: 22,
                                                    }}
                                                />
                                            ) : null}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                                justifyContent: 'space-between',
                                gap: 2,
                                borderBottom: '1px solid rgba(72,72,71,0.2)',
                                pb: 2,
                                mb: 4,
                            }}>
                            <Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 800,
                                        fontSize: { xs: 22, sm: 26 },
                                        color: '#fff',
                                    }}>
                                    Deliverables &amp; rates
                                </Typography>
                                <Typography
                                    sx={{
                                        color: CREATOR_METRIC_ON_VARIANT,
                                        fontSize: 14,
                                        fontStyle: 'italic',
                                        mt: 0.5,
                                    }}>
                                    Based on your verified {formatFollowersForInsight(followers)} reach on{' '}
                                    {RATE_CARD_PLATFORMS.find(p => p.id === platform)?.label}.
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    bgcolor: 'rgba(0,108,80,0.25)',
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 999,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: SECONDARY,
                                        boxShadow: '0 0 8px rgba(0,253,193,0.6)',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        color: SECONDARY,
                                    }}>
                                    Live market insights
                                </Typography>
                            </Box>
                        </Box>

                        {availablePlatforms.length > 1 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                {availablePlatforms.map(p => (
                                    <Button
                                        key={p.id}
                                        size="small"
                                        onClick={() => setPlatform(p.id)}
                                        startIcon={<PlatformBrandGlyph id={p.id} size={18} />}
                                        sx={{
                                            borderRadius: 999,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            px: 2,
                                            bgcolor:
                                                platform === p.id
                                                    ? 'rgba(255,201,101,0.15)'
                                                    : CREATOR_METRIC_SURFACE_HI,
                                            color: platform === p.id ? CREATOR_METRIC_GOLD : CREATOR_METRIC_ON_VARIANT,
                                            border:
                                                platform === p.id
                                                    ? `1px solid ${CREATOR_METRIC_GOLD}`
                                                    : '1px solid transparent',
                                            '& .MuiButton-startIcon': { mr: 0.75, ml: 0.25 },
                                        }}>
                                        {p.label}
                                    </Button>
                                ))}
                            </Box>
                        ) : null}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            {deliverables.map(d => {
                                const suggestion = suggestRateRange(followers, d.key);
                                return (
                                    <Box
                                        key={d.key}
                                        sx={{
                                            bgcolor: CREATOR_METRIC_SURFACE_LOW,
                                            borderRadius: '12px',
                                            border: '1px solid rgba(72,72,71,0.15)',
                                            p: { xs: 2.5, sm: 3 },
                                            transition: 'border-color 0.2s',
                                            '&:hover': { borderColor: 'rgba(236,170,0,0.25)' },
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                                gap: 3,
                                            }}>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '8px',
                                                            bgcolor: CREATOR_METRIC_SURFACE_HI,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}>
                                                        <DeliverableIcon name={d.icon} />
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            fontWeight: 800,
                                                            fontSize: 18,
                                                            color: '#fff',
                                                        }}>
                                                        {d.title}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        color: CREATOR_METRIC_ON_VARIANT,
                                                        fontSize: 14,
                                                        lineHeight: 1.6,
                                                        maxWidth: 360,
                                                        mb: 2,
                                                    }}>
                                                    {d.description}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        px: 1.5,
                                                        py: 0.75,
                                                        borderRadius: 999,
                                                        bgcolor: 'rgba(0,253,193,0.06)',
                                                        border: '1px solid rgba(0,253,193,0.12)',
                                                    }}>
                                                    <TrendingUpIcon sx={{ fontSize: 16, color: SECONDARY }} />
                                                    <Typography
                                                        sx={{
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                            letterSpacing: '0.14em',
                                                            textTransform: 'uppercase',
                                                            color: SECONDARY,
                                                        }}>
                                                        Suggested: {formatUsdRange(suggestion.min, suggestion.max)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Box sx={{ position: 'relative', mb: 2 }}>
                                                    <Typography
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -10,
                                                            left: 16,
                                                            px: 1,
                                                            bgcolor: CREATOR_METRIC_SURFACE_LOW,
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                            letterSpacing: '0.15em',
                                                            textTransform: 'uppercase',
                                                            color: CREATOR_METRIC_GOLD,
                                                        }}>
                                                        Your rate (USD)
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            bgcolor: CREATOR_METRIC_SURFACE_HI,
                                                            borderRadius: '12px',
                                                            border: '1px solid rgba(255,255,255,0.05)',
                                                            boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.35)',
                                                            '&:focus-within': {
                                                                borderColor: 'rgba(236,170,0,0.4)',
                                                            },
                                                        }}>
                                                        <Typography
                                                            sx={{
                                                                pl: 2.5,
                                                                fontSize: 28,
                                                                fontWeight: 700,
                                                                color: CREATOR_METRIC_ON_VARIANT,
                                                            }}>
                                                            $
                                                        </Typography>
                                                        <TextField
                                                            value={draftRates[d.key] ?? ''}
                                                            onChange={e => {
                                                                const v = e.target.value.replace(/[^\d.]/g, '');
                                                                setDraftRates(prev => ({ ...prev, [d.key]: v }));
                                                            }}
                                                            placeholder="0"
                                                            variant="standard"
                                                            InputProps={{
                                                                disableUnderline: true,
                                                                sx: {
                                                                    fontSize: { xs: 28, sm: 32 },
                                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                    fontWeight: 800,
                                                                    color: '#fff',
                                                                    py: 1.5,
                                                                },
                                                            }}
                                                            fullWidth
                                                        />
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1.5,
                                                        bgcolor: 'rgba(255,201,101,0.05)',
                                                        p: 1.5,
                                                        borderRadius: '8px',
                                                    }}>
                                                    <InfoOutlinedIcon
                                                        sx={{ color: CREATOR_METRIC_GOLD, fontSize: 20 }}
                                                    />
                                                    <Typography
                                                        sx={{
                                                            color: CREATOR_METRIC_ON_VARIANT,
                                                            fontSize: 13,
                                                            fontStyle: 'italic',
                                                        }}>
                                                        Market tip: {d.marketTip}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: 'rgba(38,38,38,0.6)',
                    backdropFilter: 'blur(20px)',
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography
                        sx={{
                            fontSize: 10,
                            letterSpacing: '0.2em',
                            textTransform: 'uppercase',
                            color: CREATOR_METRIC_ON_VARIANT,
                            fontWeight: 700,
                        }}>
                        Estimated card value
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontWeight: 900, fontSize: 24, color: '#fff' }}>
                        {fmtUsd(packageTotal)}{' '}
                        <Typography component="span" sx={{ fontSize: 12, color: CREATOR_METRIC_GOLD, fontWeight: 500 }}>
                            / package
                        </Typography>
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
                    {step === 2 ? (
                        <Button
                            onClick={() => setStep(1)}
                            sx={{
                                flex: { xs: 1, sm: 'none' },
                                borderRadius: CREATOR_METRIC_RADIUS_BTN,
                                px: 3,
                                py: 1.25,
                                color: '#fff',
                                border: '1px solid rgba(72,72,71,0.4)',
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            }}>
                            Back
                        </Button>
                    ) : null}
                    <Button
                        onClick={onClose}
                        sx={{
                            flex: { xs: 1, sm: 'none' },
                            borderRadius: CREATOR_METRIC_RADIUS_BTN,
                            px: 3,
                            py: 1.25,
                            color: '#fff',
                            border: '1px solid rgba(72,72,71,0.4)',
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                        }}>
                        Cancel
                    </Button>
                    <Button
                        disabled={saving || (step === 1 && availablePlatforms.length === 0)}
                        onClick={() => {
                            if (step === 1) setStep(2);
                            else handleSave();
                        }}
                        sx={{
                            flex: { xs: 2, sm: 'none' },
                            borderRadius: CREATOR_METRIC_RADIUS_BTN,
                            px: 4,
                            py: 1.25,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontWeight: 800,
                            color: '#392700',
                            background: `linear-gradient(90deg, ${CREATOR_METRIC_GOLD}, #feb700)`,
                            boxShadow: '0 10px 25px rgba(254,183,0,0.3)',
                            '&:hover': { background: `linear-gradient(90deg, #ffd080, #feb700)` },
                        }}>
                        {saving ? 'Saving…' : step === 1 ? 'Continue' : 'Save rates'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
}
